import { odooApi } from '../index';
import { Post, PaginatedResponse } from '@/types';
import { getOdooLang } from '@/config/api.config';
import { 
  transformOdooNewsToPost, 
  transformOdooPaginatedResponse,
  extractErrorMessage 
} from '../transforms';

/**
 * Posts API Endpoints (Connected to Odoo Backend)
 * 
 * This file contains all post-related API endpoints.
 * Now connected to real Odoo API at /api/moys/news
 */

export const postsApi = odooApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get paginated posts from Odoo
    getPosts: builder.query<
      PaginatedResponse<Post>,
      { 
        page?: number; 
        pageSize?: number; 
        departmentId?: number | string; 
        tagIds?: number[] | string; 
        dateFrom?: string; 
        dateTo?: string; 
        search?: string; 
        lang?: 'ar' | 'en' | 'ku' 
      }
    >({
      query: ({ page = 1, pageSize = 10, departmentId, tagIds, dateFrom, dateTo, search, lang = 'ar' }) => {
        const odooLang = getOdooLang(lang);
        
        // Build query string - backend expects page and page_size
        const params = new URLSearchParams({
          lang: odooLang,
          page: String(page),
          page_size: String(pageSize),
        });
        
        // Wire backend filters
        if (departmentId) {
          params.append('department_id', String(departmentId));
        }
        if (tagIds) {
          // Backend expects comma-separated tag IDs
          const tagIdsStr = Array.isArray(tagIds) ? tagIds.join(',') : String(tagIds);
          params.append('tag_ids', tagIdsStr);
        }
        if (dateFrom) {
          params.append('date_from', dateFrom);
        }
        if (dateTo) {
          params.append('date_to', dateTo);
        }
        if (search) {
          params.append('search', search);
        }

        return {
          url: `/api/moys/news?${params.toString()}`,
          method: 'POST',
          // No body - backend reads from query parameters
        };
      },
      transformResponse: (response: any, meta, arg): PaginatedResponse<Post> => {
        try {
          // Transform Odoo response to frontend format
          return transformOdooPaginatedResponse(
            response,
            (item, lang) => transformOdooNewsToPost(item as any, lang) as Post,
            arg.page || 1,
            arg.pageSize || 10,
            arg.lang || 'ar'
          );
        } catch (error) {
          console.error('[Posts API] Transform error:', error);
          throw error;
        }
      },
      transformErrorResponse: (response) => {
        return extractErrorMessage(response);
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Post' as const, id })),
              { type: 'Post', id: 'LIST' },
            ]
          : [{ type: 'Post', id: 'LIST' }],
    }),

    // Get single post by ID or slug
    getPost: builder.query<Post, { id: string; lang?: 'ar' | 'en' | 'ku' }>({
      async queryFn({ id, lang = 'ar' }, api, extraOptions, baseQuery) {
        const odooLang = getOdooLang(lang);

        try {
          let newsData: any = null;

          // 1) Try ID-based endpoint if the param looks numeric
          const newsId = parseInt(id, 10);
          if (!isNaN(newsId)) {
            const result = await baseQuery({
              url: `/api/moys/news/${newsId}?lang=${odooLang}`,
              method: 'GET',
            });

            if (result.error) {
              console.error('[Posts API] News ID endpoint error:', result.error);
            } else {
              const response = result.data as any;
              if (response && response.success && response.data) {
                newsData = response.data;
                console.log('[Posts API] Found post via ID endpoint:', newsId);
              }
            }
          }

          // 2) Fallback: try to resolve by slug using the paginated list
          if (!newsData) {
            console.log('[Posts API] Attempting to find news by slug:', id);

            const listResult = await baseQuery({
              url: `/api/moys/news?lang=${odooLang}&page=1&page_size=100`,
              method: 'POST',
            });

            if (listResult.error) {
              console.error('[Posts API] Error fetching news list for slug lookup:', listResult.error);
              return { error: listResult.error as any };
            }

            const listResponse = listResult.data as any;
            if (listResponse && listResponse.success && Array.isArray(listResponse.data)) {
              const found = listResponse.data.find((n: any) => n.slug === id);
              if (found) {
                newsData = found;
                console.log('[Posts API] Found news by slug:', id);
              }
            }
          }

          if (!newsData) {
            console.warn('[Posts API] Post not found by ID or slug:', id);
            return { error: { status: 404, data: 'Post not found' } as any };
          }

          // Transform the single news item
          const post = transformOdooNewsToPost(newsData, lang);
          return { data: post as Post };
        } catch (error) {
          console.error('[Posts API] Get single post error:', error);
          return { error: { status: 500, data: extractErrorMessage(error) } as any };
        }
      },
      providesTags: (result, error, { id }) => [{ type: 'Post', id }],
    }),

    /**
     * MUTATIONS - Not yet implemented in Odoo backend
     * Keeping these as placeholders for future implementation
     */

    // Create post - TODO: Implement backend endpoint
    createPost: builder.mutation<Post, Partial<Post>>({
      queryFn: async () => {
        return { error: { status: 501, data: 'Not implemented - Backend endpoint needed' } as any };
      },
      invalidatesTags: ['Post'],
    }),

    // Update post - TODO: Implement backend endpoint
    updatePost: builder.mutation<Post, { id: string; data: Partial<Post> }>({
      queryFn: async () => {
        return { error: { status: 501, data: 'Not implemented - Backend endpoint needed' } as any };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Post', id }],
    }),

    // Submit post for review - TODO: Implement backend endpoint
    submitPost: builder.mutation<Post, string>({
      queryFn: async () => {
        return { error: { status: 501, data: 'Not implemented - Backend endpoint needed' } as any };
      },
      invalidatesTags: (result, error, id) => [{ type: 'Post', id }],
    }),

    // Approve post - TODO: Implement backend endpoint
    approvePost: builder.mutation<Post, string>({
      queryFn: async () => {
        return { error: { status: 501, data: 'Not implemented - Backend endpoint needed' } as any };
      },
      invalidatesTags: (result, error, id) => [{ type: 'Post', id }],
    }),

    // Reject post - TODO: Implement backend endpoint
    rejectPost: builder.mutation<Post, string>({
      queryFn: async () => {
        return { error: { status: 501, data: 'Not implemented - Backend endpoint needed' } as any };
      },
      invalidatesTags: (result, error, id) => [{ type: 'Post', id }],
    }),
  }),
});

export const {
  useGetPostsQuery,
  useGetPostQuery,
  useCreatePostMutation,
  useUpdatePostMutation,
  useSubmitPostMutation,
  useApprovePostMutation,
  useRejectPostMutation,
} = postsApi;
